import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { api, API } from "../../lib/api";
import { useAuth, canEdit } from "../../lib/auth";
import { toast } from "sonner";
import { Inbox, Search, Download, Mail, Phone, Calendar, User2, FileText, X } from "lucide-react";
import { Modal, TextField, TextArea, SelectField } from "./SponsorsPage";

const STATUSES = ["new", "under_review", "contacted", "approved", "closed", "archived"];
const STATUS_LABEL = {
  new: "NEW", under_review: "UNDER REVIEW", contacted: "CONTACTED",
  approved: "APPROVED", closed: "CLOSED", archived: "ARCHIVED",
};
const STATUS_COLOR = {
  new: "text-aawta-pink", under_review: "text-yellow-300",
  contacted: "text-blue-300", approved: "text-green-400",
  closed: "text-white/50", archived: "text-white/30",
};

export default function InboxPage() {
  const { user } = useAuth();
  const editable = canEdit(user);
  const [types, setTypes] = useState([]);
  const [activeType, setActiveType] = useState("nominations");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const loadTypes = useCallback(async () => {
    const r = await api.get("/admin/submissions/types", { withCredentials: true });
    setTypes(r.data);
  }, []);

  const loadItems = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (statusFilter) params.set("status", statusFilter);
    const r = await api.get(`/admin/submissions/${activeType}?${params}`, { withCredentials: true });
    setItems(r.data);
  }, [activeType, q, statusFilter]);

  useEffect(() => { loadTypes(); }, [loadTypes]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const updateAdmin = async (sub, patch) => {
    try {
      const r = await api.patch(`/admin/submissions/${activeType}/${sub.id}/admin`, patch, { withCredentials: true });
      if (r.data?._promotion_message) {
        toast.success(`✓ ${r.data._promotion_message}`);
      } else {
        toast.success("Saved");
      }
      await Promise.all([loadItems(), loadTypes()]);
      if (selected?.id === sub.id) {
        setSelected({ ...sub, _admin: { ...(sub._admin || {}), ...patch, converted_to: r.data?.converted_to || sub._admin?.converted_to } });
      }
    } catch (e) { toast.error("Save failed"); }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/submissions/${activeType}/export.csv`, {
        withCredentials: true, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `aawta-${activeType}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const getName = (i) => i.full_name || i.contact_name || i.nominator_name || i.name || "—";
  const getEmail = (i) => i.email || i.nominator_email || "—";
  const getSummary = (i) => {
    if (i.nominee_name) return `${i.nominee_name} · ${i.category}`;
    if (i.organization && i.tier) return `${i.organization} · ${i.tier}`;
    if (i.organization) return i.organization;
    if (i.outlet) return `${i.outlet} · ${i.coverage_type}`;
    if (i.business_name) return i.business_name;
    if (i.table_type) return `${i.table_type} · ${i.guest_count} guests`;
    if (i.subject) return i.subject;
    if (i.payment_type) return `${i.payment_type} · $${i.amount}`;
    return "—";
  };

  const activeMeta = types.find((t) => t.type === activeType);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-overline text-aawta-pink mb-2 flex items-center gap-2"><Inbox size={14} /> SUBMISSIONS INBOX</p>
        <h1 className="font-display text-4xl text-white">Command Center</h1>
        <p className="text-white/50 mt-2 text-sm">Every form submission across the public site — triage, assign, take notes, export.</p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6 items-start">
        {/* Type sidebar */}
        <aside data-testid="inbox-types" className="border border-white/10 bg-aawta-charcoal/30">
          {types.map((t) => (
            <button
              key={t.type}
              data-testid={`type-${t.type}`}
              onClick={() => { setActiveType(t.type); setSelected(null); }}
              className={`w-full text-left px-4 py-3 border-l-2 flex items-center justify-between gap-3 transition-all ${
                activeType === t.type
                  ? "border-aawta-pink bg-aawta-pink/10"
                  : "border-transparent hover:bg-white/5"
              }`}
            >
              <span className={`text-sm ${activeType === t.type ? "text-white" : "text-white/70"}`}>{t.label}</span>
              <span className="flex items-center gap-1.5">
                {t.new > 0 && (
                  <span className="bg-aawta-pink text-white text-[10px] font-mono px-1.5 rounded">{t.new}</span>
                )}
                <span className="text-xs text-white/40 font-mono">{t.total}</span>
              </span>
            </button>
          ))}
        </aside>

        {/* Main */}
        <section>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="inbox-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, email, organization…"
                className="w-full bg-aawta-charcoal/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-9 py-2 text-sm transition-colors"
              />
            </div>
            <select
              data-testid="inbox-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-aawta-charcoal/60 border border-white/10 text-white px-3 py-2 text-sm uppercase tracking-wider"
            >
              <option value="">ALL STATUSES</option>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <button data-testid="inbox-export-btn" onClick={exportCsv} className="btn-ghost py-2.5 px-4">
              <Download size={14} /> CSV
            </button>
          </div>

          <div className="border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-aawta-charcoal/60 text-overline text-white/50 text-left">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Summary</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-white/40">
                    <Inbox className="mx-auto mb-2 text-white/30" size={24} />
                    No submissions match this view.
                  </td></tr>
                )}
                {items.map((i) => (
                  <tr key={i.id} data-testid={`row-${i.id}`} onClick={() => setSelected(i)} className="border-t border-white/5 hover:bg-white/5 cursor-pointer">
                    <td className="px-4 py-3 text-white">{getName(i)}</td>
                    <td className="px-4 py-3 text-white/70 text-xs">{getEmail(i)}</td>
                    <td className="px-4 py-3 text-white/70 text-xs">{getSummary(i)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs uppercase tracking-wider ${STATUS_COLOR[i._admin?.status || "new"]}`}>
                        {STATUS_LABEL[i._admin?.status || "new"]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">{(i.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {selected && (
        <Modal title={`${activeMeta?.label} · Detail`} onClose={() => setSelected(null)}>
          <SubmissionDetail
            sub={selected}
            coll={activeType}
            editable={editable}
            onUpdate={(patch) => updateAdmin(selected, patch)}
            onVendorRefresh={async () => {
              const r = await api.get(`/admin/vendors/${selected.id}`, { withCredentials: true });
              setSelected({ ...selected, ...r.data });
              await loadItems();
            }}
          />
        </Modal>
      )}
    </div>
  );
}

function SubmissionDetail({ sub, coll, editable, onUpdate, onVendorRefresh }) {
  const admin = sub._admin || {};
  const [draft, setDraft] = useState({
    status: admin.status || "new",
    assigned_to: admin.assigned_to || "",
    notes: admin.notes || "",
    follow_up_date: admin.follow_up_date || "",
  });

  const fields = Object.entries(sub).filter(
    ([k]) => !["id", "_admin", "type", "created_at",
               "logo_url", "logo_path", "logo_original_filename",
               "logo_approved", "promotional_permission_confirmed",
               "payment_verified", "logo_published",
               "logo_approval_updated_at", "logo_approval_updated_by",
               "logo_published_at", "logo_unpublished_at"].includes(k)
  );

  return (
    <div className="space-y-6">
      {coll === "vendors" && sub.logo_url && (
        <VendorLogoApproval vendor={sub} editable={editable} onRefresh={onVendorRefresh} />
      )}
      {/* Triage controls */}
      <div className="grid md:grid-cols-2 gap-4 p-4 border border-aawta-pink/20 bg-aawta-pink/5">
        <SelectField label="Status" value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} options={STATUSES} testId="detail-status" />
        <TextField label="Assigned To" value={draft.assigned_to} onChange={(v) => setDraft({ ...draft, assigned_to: v })} testId="detail-assigned" placeholder="email@aawta.org" />
        <label className="block">
          <span className="text-overline text-white/50 mb-1.5 block">Follow-Up Date</span>
          <input data-testid="detail-followup" type="date" value={draft.follow_up_date} onChange={(e) => setDraft({ ...draft, follow_up_date: e.target.value })}
            className="w-full bg-aawta-jet/60 border border-white/10 focus:border-aawta-pink outline-none text-white px-3 py-2 text-sm" />
        </label>
        <div className="md:col-span-2">
          <TextArea label="Internal Notes" value={draft.notes} onChange={(v) => setDraft({ ...draft, notes: v })} testId="detail-notes" />
        </div>
        {admin.converted_to && (
          <div className="md:col-span-2 border border-green-400/30 bg-green-400/5 p-3 text-sm" data-testid="detail-converted-banner">
            <p className="text-overline text-green-400 mb-1">✓ AUTO-PROMOTED</p>
            <p className="text-white/80">
              {admin.converted_to.message || `Created ${admin.converted_to.kind?.replace(/_/g, " ")}`}
              {admin.approved_by && <span className="text-white/50 text-xs"> · by {admin.approved_by} on {admin.approved_at?.slice(0, 10)}</span>}
            </p>
            {admin.converted_to.deduped && <p className="text-white/50 text-xs mt-1">(Existing record reused — no duplicate created)</p>}
          </div>
        )}
        {editable && (
          <div className="md:col-span-2 flex items-center gap-3">
            <button data-testid="detail-save-btn" onClick={() => onUpdate(draft)} className="btn-pink">
              SAVE TRIAGE
            </button>
            {draft.status === "approved" && !admin.converted_to && (
              <p className="text-xs text-aawta-pink">Setting status to <strong>Approved</strong> will auto-create a downstream record.</p>
            )}
          </div>
        )}
      </div>

      {/* Submission data */}
      <div>
        <p className="text-overline text-aawta-pink mb-3">SUBMITTED DATA</p>
        <p className="text-xs text-white/40 mb-4 flex items-center gap-3">
          <Calendar size={12} /> {sub.created_at?.slice(0, 19).replace("T", " ")}
        </p>
        <div className="space-y-2">
          {fields.map(([k, v]) => (
            <div key={k} className="grid grid-cols-3 gap-3 py-2 border-b border-white/5">
              <span className="text-overline text-white/40 col-span-1">{k.replace(/_/g, " ")}</span>
              <span className="col-span-2 text-sm text-white/85 break-words">
                {Array.isArray(v) ? v.join(", ") : String(v ?? "—")}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VendorLogoApproval({ vendor, editable, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const logoApproved = !!vendor.logo_approved;
  const permOk = !!vendor.promotional_permission_confirmed;
  const paymentOk = !!vendor.payment_verified;
  const published = !!vendor.logo_published;
  const gatesPassed = logoApproved && permOk && paymentOk;

  const patch = async (body) => {
    setSaving(true);
    try {
      await api.patch(`/admin/vendors/${vendor.id}/logo-approval`, body, { withCredentials: true });
      toast.success("Logo approval updated");
      await onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async () => {
    if (!published && !gatesPassed) {
      toast.error("All three gates must be confirmed before publishing the logo.");
      return;
    }
    await patch({ logo_published: !published });
  };

  const logoSrc = vendor.logo_url?.startsWith("http")
    ? vendor.logo_url
    : `${API}${vendor.logo_url.replace(/^\/api/, "")}`;

  return (
    <div data-testid="vendor-logo-approval" className="border border-aawta-pink/30 bg-aawta-pink/5 p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-overline text-aawta-pink">VENDOR LOGO · APPROVAL GATES</p>
          <p className="text-xs text-white/50 mt-1">
            All three gates must be confirmed before the logo may be published publicly.
          </p>
        </div>
        <span
          data-testid="logo-publish-status"
          className={`text-xs tracking-wider px-2.5 py-1 border ${
            published ? "border-green-400/50 text-green-400 bg-green-400/5" : "border-white/20 text-white/50"
          }`}
        >
          {published ? "PUBLISHED PUBLICLY" : "NOT PUBLISHED"}
        </span>
      </div>

      <div className="flex items-start gap-4">
        <img
          src={logoSrc}
          alt="Vendor logo"
          className="w-24 h-24 object-contain bg-white/10 p-2 border border-white/10 shrink-0"
          data-testid="vendor-logo-img"
        />
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-sm text-white truncate">{vendor.business_name}</p>
          <p className="text-xs text-white/50 truncate">{vendor.logo_original_filename || "logo"}</p>
          {vendor.logo_approval_updated_at && (
            <p className="text-[11px] text-white/40">
              Last updated {vendor.logo_approval_updated_at.slice(0, 10)}
              {vendor.logo_approval_updated_by && <> · by {vendor.logo_approval_updated_by}</>}
            </p>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-2.5">
        <GateCheck label="Logo Approved" checked={logoApproved} disabled={!editable || saving}
                   onChange={(v) => patch({ logo_approved: v })} testId="gate-logo-approved" />
        <GateCheck label="Promotional Permission" checked={permOk} disabled={!editable || saving}
                   onChange={(v) => patch({ promotional_permission_confirmed: v })} testId="gate-permission" />
        <GateCheck label="Payment Verified" checked={paymentOk} disabled={!editable || saving}
                   onChange={(v) => patch({ payment_verified: v })} testId="gate-payment" />
      </div>

      {editable && (
        <div className="flex items-center gap-3 pt-1 flex-wrap">
          <button
            data-testid="toggle-logo-publish"
            onClick={togglePublish}
            disabled={saving || (!published && !gatesPassed)}
            className={published ? "btn-ghost py-2 text-xs" : "btn-pink text-xs"}
          >
            {published ? "UNPUBLISH LOGO" : "PUBLISH LOGO PUBLICLY"}
          </button>
          {!gatesPassed && !published && (
            <p className="text-[11px] text-white/50">Confirm all three gates to enable publishing.</p>
          )}
        </div>
      )}
    </div>
  );
}

function GateCheck({ label, checked, onChange, disabled, testId }) {
  return (
    <label
      className={`flex items-center gap-2.5 border px-3 py-2.5 cursor-pointer transition-colors ${
        checked
          ? "border-green-400/50 bg-green-400/5 text-green-300"
          : "border-white/15 bg-aawta-jet/40 text-white/70 hover:border-white/30"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="w-4 h-4 accent-aawta-pink"
      />
      <span className="text-xs tracking-wider">{label}</span>
    </label>
  );
}

